// CookieConsent by orestbida (github.com/orestbida/cookieconsent), v2 and v3. v2 is verified against
// the live rcobchod.cz (v2.8.0, probe 2026-10-18; fixtures in test/consent.test.ts): no `CookieConsent`
// global (the API instance is private), `cc_cookie` = {"level":["necessary"],…} after reject all and
// {"level":["necessary","statistical","marketing","other"],…} after accept all — the analytics category
// is that shop's own "statistical". v3 is NOT verified against a live shop: written from the library's
// documented API and cookie formats; probe the shop's version, category names and cookie first.
//   - v3: live global `CookieConsent.validConsent()` / `acceptedCategory()`, else the cookie
//     (`categories`).
//   - v2: cookie only — its API instance comes from `initCookieConsent()` and is not a global, so
//     there is nothing live to read; the cookie (`level`) is updated on accept without a reload.
// Fail-closed side effects: on v3, from the moment the library is loaded until `CookieConsent.run()`
// has read the cookie, `validConsent()` is false, so a returning visitor is briefly denied. A shop
// configured with `useLocalStorage` (v3) keeps no cookie at all — then only the live path works.
//
// Categories are the shop's own names from its CookieConsent config (commonly `necessary`,
// `analytics`, `marketing`).
//
// Type-only imports, so Node's type stripping loads it (`pnpm test`).

import type { ConsentDetection } from "../config.ts";
import type { ConsentSources } from "./sources.ts";
import { everyCookie, failClosed, isObject, resolveCookie, resolveWindow, safeDecode } from "./sources.ts";

export interface CookieConsentOptions {
  category: string;
  // The consent cookie's name when the shop renamed it (`cookie.name` in its config). Default `cc_cookie`.
  cookieName?: string;
}

// The `cc_cookie` cookie, JSON (URL-encoded or not depending on the version; decoding is a no-op on
// raw JSON):
//   v3 `{ categories: ["necessary", "analytics"], revision, data, consentTimestamp, consentId, … }`
//   v2 `{ level: ["necessary", "analytics"], revision, data, rfc_cookie }`
// No cookie / unparseable / neither list holding the category (in any copy) → denied.
export const cookieConsentFromCookie = (cookie: string, category: string, cookieName = "cc_cookie"): boolean =>
  everyCookie(cookie, cookieName, (raw) => {
    try {
      const value: unknown = JSON.parse(safeDecode(raw));
      if (!isObject(value)) return false;
      const accepted = Array.isArray(value.categories) ? value.categories : value.level;
      return Array.isArray(accepted) && accepted.includes(category);
    } catch {
      return false;
    }
  });

// Live first (v3). Once the library is up (`validConsent` is a function) it is AUTHORITATIVE,
// including for "no valid consent": that also rejects a stale cookie (a bumped config `revision`
// re-asks the visitor), which the raw cookie read can't detect — so we don't fall back to the cookie
// then. Before the script has run, and always on v2, we read the cookie.
export const cookieConsent = ({ category, cookieName }: CookieConsentOptions, sources?: ConsentSources): ConsentDetection =>
  () =>
    failClosed(() => {
      const cc = resolveWindow(sources).CookieConsent;
      if (isObject(cc) && typeof cc.validConsent === "function") {
        if (cc.validConsent() !== true) return false;
        return typeof cc.acceptedCategory === "function" && cc.acceptedCategory(category) === true;
      }
      return cookieConsentFromCookie(resolveCookie(sources), category, cookieName);
    });
